/**
 * LastLoginSummary — poslední úspěšné a neúspěšné přihlášení a odkud.
 *
 * Filip 29. 10. 2026: „v entitách by mělo být taky poslední úspěšné
 * a neúspěšné přihlášení a odkud." Data dává `identity.entity_get`
 * (`last_login`) ze záznamu přihlášení (acm.login_events).
 */
import { useTranslation } from 'react-i18next';
import { Badge } from './Badge';
import { SectionHeader } from './SectionHeader';

export interface LastLogin {
  at: string;
  ip_address?: string | null;
  client_id?: string | null;
  identity_provider?: string | null;
  error?: string | null;
}

export interface LastLoginSummaryProps {
  success?: LastLogin | null;
  failure?: LastLogin | null;
}

function Row({ kind, login }: { kind: 'success' | 'failure'; login: LastLogin | null | undefined }) {
  const { t } = useTranslation();
  const label = kind === 'success'
    ? t('lastLogin.success', 'Úspěšné')
    : t('lastLogin.failure', 'Neúspěšné');
  const where = login
    ? [
      login.ip_address ? `IP ${login.ip_address}` : null,
      login.client_id ? t('lastLogin.app', 'aplikace {{app}}', { app: login.client_id }) : null,
      login.identity_provider
        ? t('lastLogin.via', 'přes {{idp}}', { idp: login.identity_provider })
        : null,
      kind === 'failure' && login.error ? login.error : null,
    ].filter(Boolean).join(' · ')
    : '';
  return (
    <div className='flex items-start gap-3 py-1.5 text-xs'>
      <div className='w-24 shrink-0'>
        <Badge variant={kind === 'success' ? 'success' : 'error'}>{label}</Badge>
      </div>
      {login
        ? (
          <div className='min-w-0'>
            <div className='text-gray-300'>{new Date(login.at).toLocaleString('cs')}</div>
            {where && <div className='text-gray-500 break-all'>{where}</div>}
          </div>
        )
        : <div className='text-gray-500'>{t('lastLogin.none', 'zatím žádné')}</div>}
    </div>
  );
}

export function LastLoginSummary({ success, failure }: LastLoginSummaryProps) {
  const { t } = useTranslation();
  return (
    <div>
      <SectionHeader
        title={t('lastLogin.title', 'Přihlášení')}
        description={t('lastLogin.description', 'Poslední úspěšné a neúspěšné přihlášení přes SSO a odkud.')}
      />
      <Row kind='success' login={success} />
      <Row kind='failure' login={failure} />
    </div>
  );
}
